import React, { useState, useEffect } from 'react'
import { getUsername, changePasswordApi, clearAuth } from '../auth'
import { useNavigate } from 'react-router-dom'

export default function ChangePassword() {
  const [oldPwd, setOld] = useState('')
  const [newPwd, setNew] = useState('')
  const [confirmPwd, setConfirmPwd] = useState('')
  const [msg, setMsg] = useState({ text: '', type: '' })
  const [isLoading, setIsLoading] = useState(false)
  const [passwordStrength, setPasswordStrength] = useState('')
  const [showPassword, setShowPassword] = useState({
    old: false,
    new: false,
    confirm: false
  })
  const nav = useNavigate()
  const username = getUsername()

  const validatePassword = (password) => {
    if (password.length < 8) return 'Weak - at least 8 characters needed'
    if (!/(?=.*[a-z])(?=.*[A-Z])/.test(password)) return 'Medium - add uppercase & lowercase letters'
    if (!/(?=.*\d)(?=.*[@$!%*?&])/.test(password)) return 'Strong - add numbers and special characters'
    return 'Very Strong'
  }

  useEffect(() => {
    if (newPwd) {
      setPasswordStrength(validatePassword(newPwd))
    } else {
      setPasswordStrength('')
    }
  }, [newPwd])

  async function submit(e) {
    e.preventDefault()
    
    if (!oldPwd || !newPwd || !confirmPwd) {
      setMsg({ text: 'Please fill in all fields', type: 'error' })
      return
    }
    
    if (newPwd !== confirmPwd) {
      setMsg({ text: 'New passwords do not match', type: 'error' })
      return
    }
    
    if (newPwd === oldPwd) {
      setMsg({ text: 'New password must be different from old password', type: 'error' })
      return
    }
    
    const strength = validatePassword(newPwd)
    if (strength.includes('Weak') || strength.includes('Medium')) {
      setMsg({ text: 'Please use a stronger password', type: 'error' })
      return
    }

    setIsLoading(true)
    setMsg({ text: '', type: '' })

    try {
      await changePasswordApi(username, oldPwd, newPwd)
      setMsg({ 
        text: 'Password changed successfully! Redirecting to login...', 
        type: 'success' 
      })
      clearAuth()
      setTimeout(() => nav('/login'), 1500)
    } catch(ex) {
      setMsg({ 
        text: ex.response?.data?.message || 'Change failed. Please check your old password.', 
        type: 'error' 
      })
    } finally {
      setIsLoading(false)
    }
  }

  const togglePasswordVisibility = (field) => {
    setShowPassword(prev => ({
      ...prev,
      [field]: !prev[field]
    }))
  }

  const getStrengthColor = () => {
    if (passwordStrength.includes('Weak')) return '#ff4444'
    if (passwordStrength.includes('Medium')) return '#ffbb33'
    if (passwordStrength.includes('Strong')) return '#00C851'
    return '#007E33'
  }

  // Inline styles
  const styles = {
    container: {
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      // padding: '20px'
    },
    card: {
      background: 'white',
      borderRadius: '20px',
      boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
      padding: '32px',
      width: '100%',
      maxWidth: '500px'
    },
    header: {
      textAlign: 'center',
      marginBottom: '32px'
    },
    icon: {
      width: '64px',
      height: '64px',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 16px'
    },
    title: {
      fontSize: '24px',
      fontWeight: 'bold',
      color: '#1a1a1a',
      margin: '0 0 8px 0'
    },
    subtitle: {
      color: '#666',
      margin: '0'
    },
    form: {
      display: 'flex',
      flexDirection: 'column',
      gap: '24px'
    },
    formGroup: {
      position: 'relative'
    },
    label: {
      display: 'block',
      marginBottom: '8px',
      fontWeight: '500',
      color: '#374151',
      fontSize: '14px'
    },
    inputContainer: {
      position: 'relative',
      width: '100%'
    },
    input: {
      width: '100%',
      padding: '12px 40px 12px 16px',
      border: '1px solid #d1d5db',
      borderRadius: '12px',
      fontSize: '14px',
      transition: 'all 0.2s',
      boxSizing: 'border-box'
    },
    inputFocus: {
      outline: 'none',
      borderColor: '#3b82f6',
      boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)'
    },
    showButton: {
      position: 'absolute',
      right: '12px',
      top: '50%',
      transform: 'translateY(-50%)',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      color: '#6b7280',
      padding: '0',
      width: '24px',
      height: '24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    strengthBar: {
      height: '4px',
      background: '#e5e7eb',
      borderRadius: '2px',
      overflow: 'hidden',
      marginTop: '4px'
    },
    strengthFill: {
      height: '100%',
      transition: 'width 0.3s ease'
    },
    strengthText: {
      fontSize: '12px',
      marginTop: '2px'
    },
    submitButton: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      border: 'none',
      padding: '14px 24px',
      borderRadius: '12px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'opacity 0.2s',
      fontSize: '16px',
      width: '100%'
    },
    submitButtonDisabled: {
      opacity: '0.5',
      cursor: 'not-allowed'
    },
    messageBox: {
      padding: '16px',
      borderRadius: '12px',
      margin: '16px 0',
      display: 'flex',
      alignItems: 'center'
    },
    messageSuccess: {
      background: '#d1fae5',
      color: '#065f46',
      border: '1px solid #a7f3d0'
    },
    messageError: {
      background: '#fee2e2',
      color: '#991b1b',
      border: '1px solid #fecaca'
    },
    backButton: {
      background: 'none',
      border: 'none',
      color: '#666',
      cursor: 'pointer',
      fontSize: '14px',
      textDecoration: 'underline',
      padding: '0',
      marginTop: '16px',
      alignSelf: 'center'
    },
    tipsContainer: {
      marginTop: '24px',
      paddingTop: '24px',
      borderTop: '1px solid #e5e7eb'
    },
    tipsTitle: {
      fontSize: '14px',
      fontWeight: '600',
      color: '#374151',
      marginBottom: '8px'
    },
    tipsList: {
      fontSize: '12px',
      color: '#6b7280',
      paddingLeft: '20px',
      margin: '0'
    },
    tipsItem: {
      marginBottom: '4px'
    }
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.icon}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
              <path d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" stroke="white" strokeWidth="2"/>
            </svg>
          </div>
          <h2 style={styles.title}>Change Password</h2>
          <p style={styles.subtitle}>Update your account security</p>
        </div>

        <form onSubmit={submit} style={styles.form}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {/* Current Password */}
            <div style={styles.formGroup}>
              <label style={styles.label}>Current Password</label>
              <div style={styles.inputContainer}>
                <input
                  type={showPassword.old ? "text" : "password"}
                  value={oldPwd}
                  onChange={e => setOld(e.target.value)}
                  style={styles.input}
                  placeholder="Enter current password"
                  disabled={isLoading}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#3b82f6';
                    e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                    e.target.style.outline = 'none';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#d1d5db';
                    e.target.style.boxShadow = 'none';
                  }}
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('old')}
                  style={styles.showButton}
                  title={showPassword.old ? "Hide password" : "Show password"}
                >
                  {showPassword.old ? '🙈' : '👁️'}
                </button>
              </div>
            </div>

            {/* New Password */}
            <div style={styles.formGroup}>
              <label style={styles.label}>New Password</label>
              <div style={styles.inputContainer}>
                <input
                  type={showPassword.new ? "text" : "password"}
                  value={newPwd}
                  onChange={e => setNew(e.target.value)}
                  style={styles.input}
                  placeholder="Enter new password"
                  disabled={isLoading}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#3b82f6';
                    e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                    e.target.style.outline = 'none';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#d1d5db';
                    e.target.style.boxShadow = 'none';
                  }}
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('new')}
                  style={styles.showButton}
                  title={showPassword.new ? "Hide password" : "Show password"}
                >
                  {showPassword.new ? '🙈' : '👁️'}
                </button>
              </div>
              {newPwd && (
                <div style={{ marginTop: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '4px' }}>
                    <span style={{ color: '#666' }}>Password strength:</span>
                    <span style={{ color: getStrengthColor(), fontWeight: '500' }}>
                      {passwordStrength}
                    </span>
                  </div>
                  <div style={styles.strengthBar}>
                    <div 
                      style={{
                        ...styles.strengthFill,
                        width: passwordStrength.includes('Weak') ? '33%' : 
                               passwordStrength.includes('Medium') ? '66%' : '100%',
                        backgroundColor: getStrengthColor()
                      }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div style={styles.formGroup}>
              <label style={styles.label}>Confirm New Password</label>
              <div style={styles.inputContainer}>
                <input
                  type={showPassword.confirm ? "text" : "password"}
                  value={confirmPwd}
                  onChange={e => setConfirmPwd(e.target.value)}
                  style={{
                    ...styles.input,
                    borderColor: confirmPwd && newPwd !== confirmPwd ? '#f87171' : '#d1d5db'
                  }}
                  placeholder="Confirm new password"
                  disabled={isLoading}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#3b82f6';
                    e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                    e.target.style.outline = 'none';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = confirmPwd && newPwd !== confirmPwd ? '#f87171' : '#d1d5db';
                    e.target.style.boxShadow = 'none';
                  }}
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('confirm')}
                  style={styles.showButton}
                  title={showPassword.confirm ? "Hide password" : "Show password"}
                >
                  {showPassword.confirm ? '🙈' : '👁️'}
                </button>
              </div>
              {confirmPwd && newPwd !== confirmPwd && (
                <p style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span>⚠️</span>
                  <span>Passwords don't match</span>
                </p>
              )}
            </div>
          </div>

          {/* Message */}
          {msg.text && (
            <div style={{
              ...styles.messageBox,
              ...(msg.type === 'success' ? styles.messageSuccess : styles.messageError)
            }}>
              {msg.type === 'success' ? '✅' : '❌'}
              <span style={{ marginLeft: '8px' }}>{msg.text}</span>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading}
            style={{
              ...styles.submitButton,
              ...(isLoading ? styles.submitButtonDisabled : {})
            }}
          >
            {isLoading ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid white',
                  borderTop: '2px solid transparent',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  marginRight: '8px'
                }} />
                Changing Password...
              </div>
            ) : 'Change Password'}
          </button>

          {/* Back button */}
          <button
            type="button"
            onClick={() => nav(-1)}
            style={styles.backButton}
            disabled={isLoading}
          >
            ← Back to dashboard
          </button>
        </form>

        {/* Security Tips */}
        <div style={styles.tipsContainer}>
          <h4 style={styles.tipsTitle}>💡 Password Tips</h4>
          <ul style={styles.tipsList}>
            <li style={styles.tipsItem}>Use at least 8 characters with mixed case</li>
            <li style={styles.tipsItem}>Include numbers and special characters</li>
            <li style={styles.tipsItem}>Avoid using personal information</li>
            <li style={styles.tipsItem}>Don't reuse old passwords</li>
          </ul>
        </div>
      </div>

      {/* Add CSS animation for spinner */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        button:active {
          transform: scale(0.98);
        }
        
        button:hover:not(:disabled) {
          opacity: 0.9;
        }
      `}</style>
    </div>
  )
}